import { useCallback, useEffect, useState } from 'react'
import { AnalyticsConsentCard } from './analytics/AnalyticsConsent'
import ReviewCard, { type PublicReview } from './components/ReviewCard'
import { HomepageV3Footer, HomepageV3Header } from './components/HomepageV3Chrome'
import { cartBoxCount } from './order/cartStorage'


type ReviewsPayload = {
  reviews: PublicReview[]
  page: number
  pageSize: number
  total: number
  hasMore: boolean
}


function Arrow() {
  return <span className="sg-v3-arrow" aria-hidden="true">→</span>
}

export default function ReviewsPage() {
  const [cartCount, setCartCount] = useState(() => cartBoxCount())
  const [reviews, setReviews] = useState<PublicReview[]>([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const refreshCart = () => setCartCount(cartBoxCount())
    window.addEventListener('storage', refreshCart)
    const timer = window.setInterval(refreshCart, 500)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('storage', refreshCart)
    }
  }, [])

  const loadFirstPage = useCallback(async (signal?: AbortSignal) => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams({ source: 'all', page: '1' })
      const response = await fetch(`/api/reviews?${params}`, {
        credentials: 'same-origin',
        cache: 'no-store',
        signal,
      })
      if (!response.ok) throw new Error('Customer reviews could not be loaded.')
      const payload = await response.json() as ReviewsPayload
      setReviews(Array.isArray(payload.reviews) ? payload.reviews : [])
      setPage(1)
      setHasMore(Boolean(payload.hasMore))
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === 'AbortError') return
      setReviews([])
      setPage(1)
      setHasMore(false)
      setError(caught instanceof Error ? caught.message : 'Customer reviews could not be loaded.')
    } finally {
      if (!signal?.aborted) setLoading(false)
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    void loadFirstPage(controller.signal)
    return () => controller.abort()
  }, [loadFirstPage])

  async function loadMore() {
    if (loadingMore || !hasMore) return
    setLoadingMore(true)
    setError('')
    try {
      const nextPage = page + 1
      const params = new URLSearchParams({ source: 'all', page: String(nextPage) })
      const response = await fetch(`/api/reviews?${params}`, {
        credentials: 'same-origin',
        cache: 'no-store',
      })
      if (!response.ok) throw new Error('More reviews could not be loaded.')
      const payload = await response.json() as ReviewsPayload
      setReviews((current) => [...current, ...(Array.isArray(payload.reviews) ? payload.reviews : [])])
      setPage(nextPage)
      setHasMore(Boolean(payload.hasMore))
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'More reviews could not be loaded.')
    } finally {
      setLoadingMore(false)
    }
  }

  return (
    <div className="sg-v3 reviews-page">
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <HomepageV3Header cartCount={cartCount} activeHref="/reviews" />

      <main id="main-content" tabIndex={-1}>
        <section className="reviews-hero" aria-labelledby="reviews-title">
          <div className="sg-v3-shell reviews-hero__inner">
            <p className="sg-v3-eyebrow">REVIEWS</p>
            <h1 id="reviews-title">
              <span>THE GREMLINS</span>
              <strong>HAVE SPOKEN.</strong>
            </h1>
            <p>
              Real words from real Sugar Gremlin customers.<br />
              No made-up testimonials. No polished marketing nonsense.
            </p>
          </div>
        </section>

        <section className="reviews-body" aria-label="Customer reviews">
          <div className="sg-v3-shell reviews-body__inner">
            {loading ? (
              <div className="reviews-state" role="status">
                <span className="reviews-state__spinner" aria-hidden="true" />
                <strong>Loading customer reviews…</strong>
              </div>
            ) : error && reviews.length === 0 ? (
              <div className="reviews-state reviews-state--error" role="alert">
                <p className="sg-v3-eyebrow">REVIEWS</p>
                <h2>REVIEWS ARE HIDING.</h2>
                <p>Customer reviews could not be loaded right now.</p>
                <button className="sg-v3-button sg-v3-button--outline" type="button" onClick={() => void loadFirstPage()}>
                  TRY AGAIN <Arrow />
                </button>
              </div>
            ) : reviews.length === 0 ? (
              <div className="reviews-state reviews-state--empty">
                <p className="sg-v3-eyebrow">REVIEWS</p>
                <h2>THE GREMLINS ARE QUIET. <strong>FOR NOW.</strong></h2>
                <p>Customer reviews will show up here once we&apos;ve added them.</p>
                <a className="sg-v3-button sg-v3-button--pink" href="/order">BUILD YOUR BOX <Arrow /></a>
              </div>
            ) : (
              <>
                <div className="reviews-grid">
                  {reviews.map((review) => <ReviewCard review={review} key={review.id} />)}
                </div>
                {error ? (
                  <div className="reviews-inline-error" role="alert">
                    <span>{error}</span>
                    <button type="button" onClick={() => void loadMore()}>Try again</button>
                  </div>
                ) : null}
                {hasMore ? (
                  <div className="reviews-load-more">
                    <button className="sg-v3-button sg-v3-button--outline" type="button" onClick={() => void loadMore()} disabled={loadingMore}>
                      {loadingMore ? 'LOADING…' : 'LOAD MORE'} {!loadingMore ? <Arrow /> : null}
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </section>

        <section className="reviews-cta" aria-labelledby="reviews-cta-title">
          <div className="sg-v3-shell reviews-cta__inner">
            <div>
              <p className="sg-v3-eyebrow">ENOUGH TALK.</p>
              <h2 id="reviews-cta-title">TRY THEM <strong>YOURSELF.</strong></h2>
            </div>
            <a className="sg-v3-button sg-v3-button--pink" href="/order">BUILD YOUR BOX <Arrow /></a>
          </div>
        </section>
      </main>

      <HomepageV3Footer />
      <AnalyticsConsentCard />
    </div>
  )
}
